'use client';

/**
 * Host adapter contract for the {@code GamificationPointsBadge} widget.
 *
 * <p>Same "returns null when unmounted" convention as
 * {@link NotificationsSource} — chrome must not hardcode backend URLs,
 * so the widget reads via {@link useGamificationSource} from a context
 * the host provides via {@link GamificationSourceProvider}.
 *
 * <h3>Returns null when no source is mounted</h3>
 * {@code GamificationPointsBadge} renders nothing when the source is
 * missing, so a chrome without gamification wired up doesn't show an
 * inert points pill.
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface GamificationSummary {
  /** Total points earned. */
  points: number;
  /** Optional label for the point unit, e.g. "Puan" / "XP". */
  pointName?: string;
  /** Optional icon URL for the point currency (falls back to a
   *  built-in star icon when absent). */
  iconUrl?: string;
}

export interface GamificationSource {
  fetchSummary(signal?: AbortSignal): Promise<GamificationSummary>;
}

/** Dispatched on {@code window} by the host whenever the signed-in
 *  user's point total changes (e.g. a gamification WebSocket event).
 *  {@code GamificationPointsBadge} listens for this to refetch the
 *  summary without polling. */
export const GAMIFICATION_REFRESH_EVENT = 'akademi:gamification-refresh';

const GamificationSourceContext = createContext<GamificationSource | null>(null);

export function GamificationSourceProvider({
  value,
  children,
}: {
  value: GamificationSource;
  children: ReactNode;
}) {
  return (
    <GamificationSourceContext.Provider value={value}>
      {children}
    </GamificationSourceContext.Provider>
  );
}

/** Returns the host-provided source, or {@code null} when no provider
 *  is mounted. {@code GamificationPointsBadge} renders nothing in that
 *  case. */
export function useGamificationSource(): GamificationSource | null {
  return useContext(GamificationSourceContext);
}
